import React, { useState, useEffect, useCallback } from 'react';
import { getNifty100, formatPrice, formatMarketCap, changeColor, changeSign } from '../../services/indiaAPI';
import { askJarvis } from '../../services/claudeAI';
import { THEME } from '../../styles/theme';

const S = {
  wrap:      { padding: '20px', maxWidth: '1200px', margin: '0 auto' },
  title:     { fontSize: '22px', fontWeight: '800', color: THEME.colors.blue, fontFamily: THEME.fonts.hud, letterSpacing: '2px', marginBottom: '6px' },
  sub:       { fontSize: '13px', color: 'rgba(255,255,255,0.4)', marginBottom: '20px' },

  controls:  { display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px', alignItems: 'center' },
  sortBtn:   (active) => ({ padding: '7px 14px', borderRadius: '6px', border: `1px solid ${active ? THEME.colors.blue : 'rgba(255,255,255,0.1)'}`, background: active ? 'rgba(0,212,255,0.1)' : 'transparent', color: active ? THEME.colors.blue : '#9ca3af', cursor: 'pointer', fontSize: '12px', fontWeight: '700', fontFamily: THEME.fonts.hud }),
  filterBtn: (active) => ({ padding: '7px 14px', borderRadius: '6px', border: `1px solid ${active ? '#f59e0b' : 'rgba(255,255,255,0.1)'}`, background: active ? 'rgba(245,158,11,0.1)' : 'transparent', color: active ? '#f59e0b' : '#9ca3af', cursor: 'pointer', fontSize: '12px', fontWeight: '700' }),
  spacer:    { flex: 1 },
  refreshBtn:{ padding: '7px 14px', borderRadius: '6px', border: `1px solid rgba(0,212,255,0.2)`, background: 'transparent', color: THEME.colors.blue, cursor: 'pointer', fontSize: '12px' },

  // AI Picks card
  picksCard: { background: 'rgba(0,212,255,0.04)', border: '1px solid rgba(0,212,255,0.2)', borderRadius: '12px', padding: '20px', marginBottom: '20px' },
  picksTitle:{ fontSize: '12px', fontWeight: '700', letterSpacing: '2px', color: THEME.colors.blue, marginBottom: '14px', fontFamily: THEME.fonts.hud },
  picksBtn:  { padding: '10px 20px', borderRadius: '8px', background: THEME.colors.blue, color: '#000', border: 'none', cursor: 'pointer', fontWeight: '700', fontSize: '13px', fontFamily: THEME.fonts.hud },
  picksText: { fontSize: '14px', color: '#e2e8f0', lineHeight: '1.8', whiteSpace: 'pre-wrap' },

  // Table
  tableWrap: { overflowX: 'auto' },
  table:     { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th:        { textAlign: 'left', padding: '10px 12px', fontSize: '10px', fontWeight: '700', letterSpacing: '1px', color: 'rgba(255,255,255,0.3)', borderBottom: '1px solid rgba(255,255,255,0.08)', cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none' },
  td:        { padding: '10px 12px', borderBottom: '1px solid rgba(255,255,255,0.04)', whiteSpace: 'nowrap' },
  trHover:   { background: 'rgba(255,255,255,0.02)' },
  badge:     (color) => ({ display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '9px', fontWeight: '700', letterSpacing: '1px', background: color + '22', color, border: `1px solid ${color}33`, marginLeft: '4px' }),

  bar:       (pct, color) => ({ display: 'inline-block', width: `${Math.min(Math.abs(pct), 100)}%`, height: '4px', background: color, borderRadius: '2px', verticalAlign: 'middle', marginLeft: '4px' }),

  loading:   { textAlign: 'center', padding: '60px', color: THEME.colors.textMuted },
  signal:    (s) => ({
    display: 'inline-block', padding: '3px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: '700',
    background: s === 'BUY' ? '#10b98122' : s === 'SELL' ? '#ef444422' : '#f59e0b22',
    color:      s === 'BUY' ? '#10b981'   : s === 'SELL' ? '#ef4444'   : '#f59e0b',
    border:     `1px solid ${s === 'BUY' ? '#10b98133' : s === 'SELL' ? '#ef444433' : '#f59e0b33'}`,
  }),
};

// Simple signal logic (no AI — rule-based for instant screening)
function calcSignal(s) {
  if (!s.changePct || !s.week52Pos) return 'WATCH';
  const bullish = s.changePct > 0.5 && s.week52Pos > 50;
  const bearish  = s.changePct < -0.5 && s.week52Pos < 50;
  if (bullish) return 'BUY';
  if (bearish)  return 'SELL';
  return 'WATCH';
}

function volumeRatio(s) {
  if (!s.volume || !s.avgVolume) return null;
  return s.volume / s.avgVolume;
}

const SORTS = [
  { id: 'changePct',  label: '% Change' },
  { id: 'week52Pos',  label: '52W Position' },
  { id: 'volRatio',   label: 'Volume Surge' },
  { id: 'marketCap',  label: 'Market Cap' },
];

const FILTERS = [
  { id: 'all',     label: 'All 100' },
  { id: 'nifty50', label: 'Nifty 50' },
  { id: 'next50',  label: 'Next 50' },
  { id: 'fno',     label: 'F&O Only' },
  { id: 'gainers', label: 'Gainers' },
  { id: 'losers',  label: 'Losers' },
];

export default function StockScreener() {
  const [stocks,   setStocks]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [sortBy,   setSortBy]   = useState('changePct');
  const [sortDir,  setSortDir]  = useState(-1); // -1 = desc
  const [filter,   setFilter]   = useState('all');
  const [picks,    setPicks]    = useState('');
  const [picksLoading, setPicksLoading] = useState(false);
  const [watchlist, setWatchlist] = useState(() => {
    try { return JSON.parse(localStorage.getItem('jarvis_india_watchlist') || '[]'); } catch { return []; }
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getNifty100();
      const enriched = data
        .filter(s => !s.error)
        .map(s => ({ ...s, signal: calcSignal(s), volRatio: volumeRatio(s) }));
      setStocks(enriched);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleWatch = (symbol) => {
    const next = watchlist.includes(symbol)
      ? watchlist.filter(s => s !== symbol)
      : [...watchlist, symbol];
    setWatchlist(next);
    localStorage.setItem('jarvis_india_watchlist', JSON.stringify(next));
  };

  const sortAndFilter = (data) => {
    let out = [...data];
    if (filter === 'nifty50')  out = out.filter(s => s.niftyIndex === 'NIFTY50');
    if (filter === 'next50')   out = out.filter(s => s.niftyIndex === 'NIFTY_NEXT50');
    if (filter === 'fno')      out = out.filter(s => s.isFnO);
    if (filter === 'gainers')  out = out.filter(s => s.changePct > 0);
    if (filter === 'losers')   out = out.filter(s => s.changePct < 0);
    out.sort((a, b) => {
      const av = a[sortBy] ?? -Infinity;
      const bv = b[sortBy] ?? -Infinity;
      return sortDir * (bv - av);
    });
    return out;
  };

  const handleSort = (id) => {
    if (sortBy === id) setSortDir(d => -d);
    else { setSortBy(id); setSortDir(-1); }
  };

  const getTopPicks = async () => {
    setPicksLoading(true);
    setPicks('');
    const top20 = sortAndFilter(stocks)
      .slice(0, 20)
      .map(s => `${s.name} (${s.symbol.replace('.NS','')}): ₹${s.price?.toFixed(0)} | ${changeSign(s.changePct)}${s.changePct?.toFixed(2)}% | 52W:${s.week52Pos}% | Vol:${s.volRatio?.toFixed(1)}x | ${s.sector} | ${s.isFnO ? 'F&O Lot:'+s.lotSize : 'EQ'}`)
      .join('\n');

    const prompt = `You are JARVIS, Indian stock market expert. Today's data for top 20 stocks from Nifty 100:

${top20}

Pick the TOP 3 stocks Sir should watch today for trading. For each:
1. Stock name + symbol
2. Why it's interesting (momentum, level, volume)
3. Entry zone (₹)
4. Stop loss (₹)
5. Target (₹)
6. Why today specifically

Then give a 1-line overall market bias (bullish/bearish/mixed) based on these top movers.

Be concise, specific, address Sir directly.`;

    try {
      const resp = await askJarvis([{ role: 'user', content: prompt }], '', 1000);
      setPicks(resp);
    } catch (e) {
      setPicks('Error: ' + e.message);
    }
    setPicksLoading(false);
  };

  const displayed = sortAndFilter(stocks);

  return (
    <div style={S.wrap}>
      <div style={S.title}>STOCK SCREENER</div>
      <div style={S.sub}>Nifty 50 + Nifty Next 50 · Live data · Sort, filter, and get AI picks</div>

      {/* AI Daily Picks */}
      <div style={S.picksCard}>
        <div style={S.picksTitle}>JARVIS TOP 3 PICKS TODAY</div>
        {!picks && !picksLoading && (
          <button style={S.picksBtn} onClick={getTopPicks} disabled={loading}>
            {loading ? 'Loading data...' : 'Get Top 3 Picks for Today'}
          </button>
        )}
        {picksLoading && <div style={{ color: THEME.colors.textMuted }}>JARVIS is scanning 100 stocks…</div>}
        {picks && (
          <>
            <div style={S.picksText}>{picks}</div>
            <button style={{ ...S.picksBtn, marginTop: '12px', background: 'transparent', color: THEME.colors.blue, border: `1px solid ${THEME.colors.blue}44` }} onClick={getTopPicks}>Refresh Picks</button>
          </>
        )}
      </div>

      {/* Controls */}
      <div style={S.controls}>
        {FILTERS.map(f => (
          <button key={f.id} style={S.filterBtn(filter === f.id)} onClick={() => setFilter(f.id)}>{f.label}</button>
        ))}
        <div style={S.spacer}/>
        <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.3)' }}>Sort by:</span>
        {SORTS.map(s => (
          <button key={s.id} style={S.sortBtn(sortBy === s.id)} onClick={() => handleSort(s.id)}>
            {s.label} {sortBy === s.id ? (sortDir === -1 ? '↓' : '↑') : ''}
          </button>
        ))}
        <button style={S.refreshBtn} onClick={load}>Refresh</button>
      </div>

      {loading ? (
        <div style={S.loading}>Loading Nifty 100 data (first load ~15s)…</div>
      ) : (
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>★</th>
                <th style={S.th}>STOCK</th>
                <th style={{ ...S.th, textAlign: 'right' }} onClick={() => handleSort('changePct')}>% CHG {sortBy==='changePct'?(sortDir===-1?'↓':'↑'):''}</th>
                <th style={{ ...S.th, textAlign: 'right' }}>PRICE</th>
                <th style={{ ...S.th, textAlign: 'right' }} onClick={() => handleSort('week52Pos')}>52W POS {sortBy==='week52Pos'?(sortDir===-1?'↓':'↑'):''}</th>
                <th style={{ ...S.th, textAlign: 'right' }} onClick={() => handleSort('volRatio')}>VOL SURGE {sortBy==='volRatio'?(sortDir===-1?'↓':'↑'):''}</th>
                <th style={{ ...S.th, textAlign: 'right' }} onClick={() => handleSort('marketCap')}>MKTCAP {sortBy==='marketCap'?(sortDir===-1?'↓':'↑'):''}</th>
                <th style={S.th}>SECTOR</th>
                <th style={S.th}>SIGNAL</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map(s => {
                const isWatched = watchlist.includes(s.symbol);
                return (
                  <tr key={s.symbol}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={S.td}>
                      <span style={{ cursor: 'pointer', color: isWatched ? '#f59e0b' : 'rgba(255,255,255,0.2)', fontSize: '14px' }}
                        onClick={() => toggleWatch(s.symbol)}>★</span>
                    </td>
                    <td style={S.td}>
                      <div style={{ fontWeight: '600', color: '#e2e8f0' }}>{s.shortName || s.name}</div>
                      <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)' }}>
                        {s.symbol.replace('.NS','')}
                        <span style={S.badge(s.niftyIndex === 'NIFTY50' ? '#00d4ff' : '#7c3aed')}>
                          {s.niftyIndex === 'NIFTY50' ? 'N50' : 'N100'}
                        </span>
                        {s.isFnO && <span style={S.badge('#f59e0b')}>F&O</span>}
                      </div>
                    </td>
                    <td style={{ ...S.td, textAlign: 'right', color: changeColor(s.changePct), fontWeight: '700' }}>
                      {s.changePct != null ? `${changeSign(s.changePct)}${s.changePct.toFixed(2)}%` : '—'}
                    </td>
                    <td style={{ ...S.td, textAlign: 'right', fontWeight: '700', color: '#fff' }}>
                      {formatPrice(s.price)}
                    </td>
                    <td style={{ ...S.td, textAlign: 'right' }}>
                      {s.week52Pos != null ? (
                        <div>
                          <span style={{ color: s.week52Pos > 70 ? '#10b981' : s.week52Pos < 30 ? '#ef4444' : '#f59e0b', fontWeight: '700' }}>
                            {s.week52Pos}%
                          </span>
                          <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: '2px', height: '4px', width: '60px', marginTop: '3px', display: 'inline-block', marginLeft: '6px', verticalAlign: 'middle' }}>
                            <div style={{ width: `${s.week52Pos}%`, height: '100%', background: s.week52Pos > 70 ? '#10b981' : s.week52Pos < 30 ? '#ef4444' : '#f59e0b', borderRadius: '2px' }}/>
                          </div>
                        </div>
                      ) : '—'}
                    </td>
                    <td style={{ ...S.td, textAlign: 'right', color: s.volRatio > 1.5 ? '#f59e0b' : '#9ca3af', fontWeight: s.volRatio > 1.5 ? '700' : '400' }}>
                      {s.volRatio ? s.volRatio.toFixed(1) + 'x' : '—'}
                    </td>
                    <td style={{ ...S.td, textAlign: 'right', color: 'rgba(255,255,255,0.6)' }}>
                      {formatMarketCap(s.marketCap)}
                    </td>
                    <td style={{ ...S.td, color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>{s.sector}</td>
                    <td style={S.td}><span style={S.signal(s.signal)}>{s.signal}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.25)', marginTop: '12px', textAlign: 'right' }}>
            {displayed.length} stocks · Signal = rule-based (momentum + 52W position) · Use JARVIS AI for full analysis
          </div>
        </div>
      )}
    </div>
  );
}
